import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogIn, Lock, User } from 'lucide-react';
import { useLanguageStore } from '../store/useLanguageStore';
import { useAuthStore } from '../store/useAuthStore';

const Login: React.FC = () => {
  const { t } = useLanguageStore();
  const { login } = useAuthStore();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Simulate API call
    setTimeout(() => {
      const success = login(username, password);
      if (success) {
        navigate('/admin');
      } else {
        setError(t('invalid-credentials', 'Invalid username or password', 'වලංගු නොවන පරිශීලක නම හෝ මුරපදය'));
      }
      setIsLoading(false);
    }, 500);
  };

  return (
    <div className="min-h-screen bg-warm-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 lg:py-20 flex items-center justify-center min-h-[calc(100vh-200px)]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-primary-green to-secondary-blue rounded-full flex items-center justify-center mx-auto mb-4">
                <LogIn className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-text-dark mb-2">
                {t('staff-login', 'Staff Login', 'කාර්ය මණ්ඩල පිවිසීම')}
              </h2>
              <p className="text-text-secondary">
                {t('login-subtitle', 'Access the admin dashboard', 'පරිපාලන උපකරණ පුවරුවට ප්‍රවේශ වන්න')}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-error-red/10 border border-error-red/30 text-error-red px-4 py-3 rounded-lg text-sm"
                >
                  {error}
                </motion.div>
              )}

              <div>
                <label className="block text-sm font-medium text-text-dark mb-2">
                  {t('username', 'Username', 'පරිශීලක නම')}
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-secondary" />
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent"
                    placeholder={t('enter-username', 'Enter username', 'පරිශීලක නම ඇතුළත් කරන්න')}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-dark mb-2">
                  {t('password', 'Password', 'මුරපදය')}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-secondary" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent"
                    placeholder={t('enter-password', 'Enter password', 'මුරපදය ඇතුළත් කරන්න')}
                    required
                  />
                </div>
              </div>

              <motion.button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-primary-green to-secondary-blue text-white py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                whileHover={{ scale: isLoading ? 1 : 1.02 }}
                whileTap={{ scale: isLoading ? 1 : 0.98 }}
              >
                {isLoading 
                  ? t('logging-in', 'Logging in...', 'පිවිසෙමින්...')
                  : t('login', 'Login', 'පිවිසෙන්න')
                }
              </motion.button>
            </form>

            <div className="mt-6 text-center text-sm text-text-secondary">
              <p>
                {t('forgot-password', 'Forgot your password? Contact administrator', 'මුරපදය අමතකද? පරිපාලකයා සම්බන්ධ කරන්න')}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;

